import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useFormik} from "formik";
import {provjeriNormativeArtikla, saveZapisiStavke} from "../../../../api/zapisiProizvodnje";
import notifyToast from "../../../../components/Toast/Toast";
import LoadingComponent from "../../../../components/LoadingComponent/LoadingComponent";
import clsx from "clsx";
import useDebounce from "../../../../hooks/useDebounce";
import {rolnaDetaljiProizvodnjaV2} from "../../../../api/fmkNalozi";
import QrCodeScanner from "../../../../components/QrCodeScanner/QrCodeScanner";

interface ProfilacijaRolneProps {
    id: any;
    brojNaloga: any;
}

/** Zebra + general cleanup */
const normalizeScan = (value: string) =>
    String(value ?? "")
        .replace(/[\r\n\t]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

/** Robust company match: "Alternativa doo", "Alternativa doo.", "ALTERNATIVA   DOO" etc. */
const isCompanyAlternativaDoo = (s: string) => {
    const cleaned = normalizeScan(s)
        .toLowerCase()
        .replace(/[^a-z]/g, ""); // remove spaces, dots, etc.
    return cleaned === "alternativadoo";
};

/**
 * ✅ Full rolna QR payload:
 * Accepts: "77927; ...; ...; ...; Alternativa doo" (+ optional whitespace / dot / trailing ';')
 * - split by ';'
 * - ignore empty parts (handles trailing ';')
 * - need at least 5 parts
 * - last meaningful part must be Alternativa doo (robust compare)
 */
const isFullRolnaQr = (raw: string) => {
    const s = normalizeScan(raw);
    if (!s) return false;

    const parts = s
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean);

    if (parts.length < 5) return false;

    const last = parts[parts.length - 1] ?? "";
    return isCompanyAlternativaDoo(last);
};

/** If scanner provides only ID (your QrCodeScanner sometimes) -> treat as scanned */
const isIdOnly = (raw: string) => /^\d+$/.test(normalizeScan(raw));

/**
 * Zebra keyboard-wedge scanning:
 * - characters arrive very quickly
 * - usually ends with Enter/Tab
 * We'll buffer keys and call onScan when Enter/Tab arrives.
 */
function useKeyboardScanner(onScan: (value: string) => void) {
    const bufferRef = useRef("");
    const lastTsRef = useRef<number>(0);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const ts = Date.now();
            const delta = ts - lastTsRef.current;
            lastTsRef.current = ts;

            // if slow typing, reset buffer (manual)
            if (delta > 60) bufferRef.current = "";

            // finish scan on Enter/Tab
            if (e.key === "Enter" || e.key === "Tab") {
                const buf = bufferRef.current;
                bufferRef.current = "";
                if (buf && buf.length >= 6) onScan(buf);
                return;
            }

            // ignore control keys
            if (e.key.length !== 1) return;

            bufferRef.current += e.key;
        };

        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onScan]);
}

const ROLNA_SCANNED_MSG = "Rolna skenirana";
const ROLNA_NOT_SCANNED_MSG =
    "Rolna nije skenirana! Molimo skenirajte QR kod. Skeniranje rolne se prati i bice vezano za vas racun!";

const ProfilacijaRolne: React.FC<ProfilacijaRolneProps> = ({id, brojNaloga}) => {
    const initialValues = {
        "ROLNA-G": "",
        "POTVRDJUJEM-KORISTENJE-ROLNE-G": "",
        "ROLNA-D": "",
        "POTVRDJUJEM-KORISTENJE-ROLNE-D": "",

        // hidden meta (1/0)
        "ROLNA-G-SKENIRANA": 0,
        "ROLNA-D-SKENIRANA": 0,
    };

    const [loading, setLoading] = useState(false);

    const [rolneGId, setRolneGId] = useState("");
    const debouncedRolneGId = useDebounce(rolneGId);
    const [rolnaGDetalji, setRolnaGDetalji] = useState("");

    const [rolneDId, setRolneDId] = useState("");
    const debouncedRolneDId = useDebounce(rolneDId);
    const [rolnaDDetalji, setRolnaDDetalji] = useState("");

    // raw scan payload (only set by scanner events OR full-QR content in input)
    const [rolnaGRawScan, setRolnaGRawScan] = useState("");
    const [rolnaDRawScan, setRolnaDRawScan] = useState("");

    // marks that scan came from scanner (QrCodeScanner / Zebra wedge / full-QR content)
    const [rolnaGScannedByDevice, setRolnaGScannedByDevice] = useState(false);
    const [rolnaDScannedByDevice, setRolnaDScannedByDevice] = useState(false);

    // show status only after user interacts
    const [rolnaGInteracted, setRolnaGInteracted] = useState(false);
    const [rolnaDInteracted, setRolnaDInteracted] = useState(false);

    // normative state
    const [normativeLoading, setNormativeLoading] = useState(false);
    const [nalogData, setNalogData] = useState<any>(null);

    const debljinaGore = nalogData?.debljina_gore?.debljina ?? "";
    const debljinaDole = nalogData?.debljina_dole?.debljina ?? "";

    const [tolerancijaValjanjaGore, setTolerancijaValjanjaGore] = useState("");
    const [tolerancijaValjanjaDole, setTolerancijaValjanjaDole] = useState("");

    // track last-focused input so Zebra works even if activeElement is weird
    const lastFocusedRef = useRef<"G" | "D">("G");

    // optional: auto focus to reduce "must click" friction
    const rolnaGInputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        rolnaGInputRef.current?.focus();
    }, []);

    // fetch required thicknesses
    useEffect(() => {
        if (!id && !brojNaloga) return;

        const run = async () => {
            setNormativeLoading(true);
            try {
                const params = {idZapisa: id, brojNaloga};
                const res = await provjeriNormativeArtikla(params);
                const payload = res?.data;

                if (payload?.error === 0 && payload?.data?.nalogData) {
                    setNalogData(payload.data.nalogData);
                } else {
                    setNalogData(null);
                }
            } catch {
                setNalogData(null);
            } finally {
                setNormativeLoading(false);
            }
        };

        run();
    }, [id, brojNaloga]);

    // ---------------- Formik ----------------
    const formik = useFormik({
        initialValues,
        onSubmit: async (values, {setStatus, setSubmitting, resetForm}) => {
            setLoading(true);
            try {
                const data: {IdZapisa: any; BrojNaloga: any; MetaKey: string; MetaValue: any}[] = [];

                Object.entries(values).forEach(([key, value]) => {
                    const isEmpty = value === "" || value === null || value === undefined;
                    if (!isEmpty) {
                        data.push({
                            IdZapisa: id,
                            BrojNaloga: brojNaloga,
                            MetaKey: key,
                            MetaValue: value,
                        });
                    }
                });

                const response = await saveZapisiStavke(data);
                const responseData = response;

                if (responseData.error === 0) {
                    notifyToast(true, responseData.message);

                    resetForm();

                    // reset UI
                    setRolnaGDetalji("");
                    setRolnaDDetalji("");
                    setTolerancijaValjanjaGore("");
                    setTolerancijaValjanjaDole("");

                    // reset raw scans
                    setRolnaGRawScan("");
                    setRolnaDRawScan("");
                    setRolnaGScannedByDevice(false);
                    setRolnaDScannedByDevice(false);

                    // reset interaction flags
                    setRolnaGInteracted(false);
                    setRolnaDInteracted(false);

                    // reset ids
                    setRolneGId("");
                    setRolneDId("");
                } else {
                    notifyToast(false, responseData.message || "An unexpected error occurred.");
                }
            } catch (error: any) {
                notifyToast(false, error?.response?.data?.message);
                setStatus(error);
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        },
    });

    // ---------------- Scanner apply helpers ----------------
    const applyRolnaGScan = useCallback(
        (raw: string) => {
            const cleaned = normalizeScan(raw);

            setRolnaGScannedByDevice(true);
            setRolnaGRawScan(cleaned);
            setRolnaGInteracted(true);

            const idOnly = (cleaned.split(";")[0] ?? "").trim() || cleaned;
            setRolneGId(idOnly);
            formik.setFieldValue("ROLNA-G", idOnly);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const applyRolnaDScan = useCallback(
        (raw: string) => {
            const cleaned = normalizeScan(raw);

            setRolnaDScannedByDevice(true);
            setRolnaDRawScan(cleaned);
            setRolnaDInteracted(true);

            const idOnly = (cleaned.split(";")[0] ?? "").trim() || cleaned;
            setRolneDId(idOnly);
            formik.setFieldValue("ROLNA-D", idOnly);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    // Zebra wedge scans: route to active input, otherwise lastFocused
    useKeyboardScanner((value) => {
        const active = document.activeElement as HTMLElement | null;

        if (active?.id === "ROLNA-G") return applyRolnaGScan(value);
        if (active?.id === "ROLNA-D") return applyRolnaDScan(value);

        if (lastFocusedRef.current === "G") return applyRolnaGScan(value);
        return applyRolnaDScan(value);
    });

    // computed scan flags (true only if scannedByDevice + (idOnly OR full-QR))
    const rolnaGSkenirana = useMemo(() => {
        if (!rolnaGScannedByDevice) return false;
        return isIdOnly(rolnaGRawScan) || isFullRolnaQr(rolnaGRawScan);
    }, [rolnaGRawScan, rolnaGScannedByDevice]);

    const rolnaDSkenirana = useMemo(() => {
        if (!rolnaDScannedByDevice) return false;
        return isIdOnly(rolnaDRawScan) || isFullRolnaQr(rolnaDRawScan);
    }, [rolnaDRawScan, rolnaDScannedByDevice]);

    // sync hidden form fields
    useEffect(() => {
        formik.setFieldValue("ROLNA-G-SKENIRANA", rolnaGSkenirana ? 1 : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rolnaGSkenirana]);

    useEffect(() => {
        formik.setFieldValue("ROLNA-D-SKENIRANA", rolnaDSkenirana ? 1 : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rolnaDSkenirana]);

    // ---------------- Fetch rolna details: G ----------------
    useEffect(() => {
        if (debouncedRolneGId !== "") {
            rolnaDetaljiProizvodnjaV2({rolnaId: debouncedRolneGId})
                .then((response) => {
                    const rolnaDebljina = String(response?.data?.ReprMatDebljina ?? "").trim();
                    const requiredDebljina = String(debljinaGore ?? "").trim();

                    if (rolnaDebljina && requiredDebljina && rolnaDebljina !== requiredDebljina) {
                        alert(
                            `⚠️ NEODGOVARAJUĆA DEBLJINA – GORNJA ROLNA\n\n` +
                            `Potrebna debljina: ${requiredDebljina} mm\n` +
                            `Debljina rolne: ${rolnaDebljina} mm`
                        );
                    }

                    const msg = `Težina: ${response?.data?.ReprMatTrenutnaKolicina || "N/A"} | Debljina: ${
                        response?.data?.ReprMatDebljina || "N/A"
                    } | Širina: ${response?.data?.ReprMatSirina || "N/A"} | Proizvođač: ${
                        response?.data?.proizvodjac?.PartnerNaziv || "N/A"
                    } | Boja: ${response?.data?.boja_lica?.BojaNaziv || "N/A"} | Šarža: ${
                        response?.data?.stavka?.ugovor?.BrojUgovora ?? "N/A"
                    } / ${response?.data?.stavka?.ManualId ?? "N/A"}`;

                    setRolnaGDetalji(msg);
                    setTolerancijaValjanjaGore(response?.data?.ReprMatTolerancijaValjanja ?? "0");
                })
                .catch(() => {
                    setRolnaGDetalji("Rolna nije dostupna.");
                });
        } else {
            setRolnaGDetalji("");
            setTolerancijaValjanjaGore("");
        }
    }, [debouncedRolneGId, debljinaGore]);

    // ---------------- Fetch rolna details: D ----------------
    useEffect(() => {
        if (debouncedRolneDId !== "") {
            rolnaDetaljiProizvodnjaV2({rolnaId: debouncedRolneDId})
                .then((response) => {
                    const rolnaDebljina = String(response?.data?.ReprMatDebljina ?? "").trim();
                    const requiredDebljina = String(debljinaDole ?? "").trim();

                    if (rolnaDebljina && requiredDebljina && rolnaDebljina !== requiredDebljina) {
                        alert(
                            `⚠️ NEODGOVARAJUĆA DEBLJINA – DONJA ROLNA\n\n` +
                            `Potrebna debljina: ${requiredDebljina} mm\n` +
                            `Debljina rolne: ${rolnaDebljina} mm`
                        );
                    }

                    const msg = `Težina: ${response?.data?.ReprMatTrenutnaKolicina || "N/A"} | Debljina: ${
                        response?.data?.ReprMatDebljina || "N/A"
                    } | Širina: ${response?.data?.ReprMatSirina || "N/A"} | Proizvođač: ${
                        response?.data?.proizvodjac?.PartnerNaziv || "N/A"
                    } | Boja: ${response?.data?.boja_lica?.BojaNaziv || "N/A"} | Šarža: ${
                        response?.data?.stavka?.ugovor?.BrojUgovora ?? "N/A"
                    } / ${response?.data?.stavka?.ManualId ?? "N/A"}`;

                    setRolnaDDetalji(msg);
                    setTolerancijaValjanjaDole(response?.data?.ReprMatTolerancijaValjanja ?? "0");
                })
                .catch(() => {
                    setRolnaDDetalji("Rolna nije dostupna.");
                });
        } else {
            setRolnaDDetalji("");
            setTolerancijaValjanjaDole("");
        }
    }, [debouncedRolneDId, debljinaDole]);

    // ---------------- Required quantity ----------------
    const potrebnaKolicinaGore = useMemo(() => {
        return Math.ceil(Number(nalogData?.kolicina || 0) * Number(tolerancijaValjanjaGore || 0));
    }, [nalogData?.kolicina, tolerancijaValjanjaGore]);

    const potrebnaKolicinaDole = useMemo(() => {
        return Math.ceil(Number(nalogData?.kolicina || 0) * Number(tolerancijaValjanjaDole || 0));
    }, [nalogData?.kolicina, tolerancijaValjanjaDole]);

    // ---------------- UI blocks ----------------
    const ThicknessBanner = () => (
        <div
            className={clsx("alert mb-4 mt-6 d-flex align-items-center justify-content-between flex-wrap gap-2", {
                "alert-secondary": normativeLoading,
                "alert-info": !normativeLoading && (debljinaGore || debljinaDole),
                "alert-warning": !normativeLoading && !debljinaGore && !debljinaDole,
            })}
            role="alert"
        >
            {normativeLoading ? (
                <div className="d-flex align-items-center gap-2">
                    <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                    <span>Učitavam debljine...</span>
                </div>
            ) : debljinaGore || debljinaDole ? (
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <strong>Potrebno koristiti sljedece debljine:</strong>
                    <span className="badge bg-light text-dark border">
            Gore (lice): <strong>{debljinaGore || "N/A"} mm</strong>
          </span>
                    <span className="badge bg-light text-dark border">
            Dole (nalicje): <strong>{debljinaDole || "N/A"} mm</strong>
          </span>
                </div>
            ) : (
                <div>
                    <strong>Debljine potrebno koristiti:</strong> nema podataka za ovaj nalog.
                </div>
            )}
        </div>
    );

    const ScanStatus = ({interacted, scanned}: {interacted: boolean; scanned: boolean}) => {
        if (!interacted) return null;
        return scanned ? (
            <div className="alert alert-success py-2 mb-0 d-flex align-items-center justify-content-between">
                <span className="fw-semibold">{ROLNA_SCANNED_MSG}</span>
                <span className="badge bg-success">OK</span>
            </div>
        ) : (
            <div className="alert alert-danger py-2 mb-0">
                <div className="fw-semibold mb-1">Rolna nije skenirana</div>
                <div className="small">{ROLNA_NOT_SCANNED_MSG}</div>
            </div>
        );
    };

    // ---------------- Render ----------------
    return (
        <div className="container-fluid">
            {loading ? (
                <LoadingComponent />
            ) : (
                <form onSubmit={formik.handleSubmit}>
                    <ThicknessBanner />

                    <div className="row g-4" id="profilacija-rolne">
                        {/* ===================== ROLNA G ===================== */}
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge bg-primary">G</span>
                                        <h5 className="mb-0">Gornja rolna (lice)</h5>
                                    </div>

                                    <div className="d-flex align-items-center gap-2">
                                        {rolnaGInteracted ? (
                                            rolnaGSkenirana ? (
                                                <span className="badge bg-success">Skenirana</span>
                                            ) : (
                                                <span className="badge bg-danger">Nije skenirana</span>
                                            )
                                        ) : (
                                            <span className="badge bg-light text-dark border">Nije provjereno</span>
                                        )}
                                    </div>
                                </div>

                                <div className="card-body">
                                    <div className="row g-3 align-items-end">
                                        <div className="col-12 col-md-2">
                                            <div className="d-flex flex-column gap-2">
                                                <div className="small text-muted fw-semibold">Skeniraj QR kamerom</div>
                                                <QrCodeScanner
                                                    onScan={(data) => {
                                                        // camera scanner event => scanned = true (even if only ID)
                                                        applyRolnaGScan(data);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-4">
                                            <label className="form-label fw-bold">ROLNA G</label>
                                            <input
                                                ref={rolnaGInputRef}
                                                type="text"
                                                className="form-control"
                                                id="ROLNA-G"
                                                {...formik.getFieldProps("ROLNA-G")}
                                                placeholder="Upišite broj rolne"
                                                onFocus={() => (lastFocusedRef.current = "G")}
                                                onChange={(e) => {
                                                    setRolnaGInteracted(true);

                                                    const raw = e.target.value;
                                                    const cleaned = normalizeScan(raw);

                                                    // ✅ If Zebra (or paste) produced full QR -> mark as scanned
                                                    if (isFullRolnaQr(cleaned)) {
                                                        setRolnaGScannedByDevice(true);
                                                        setRolnaGRawScan(cleaned);

                                                        const idOnly = (cleaned.split(";")[0] ?? "").trim();
                                                        setRolneGId(idOnly);
                                                        formik.setFieldValue("ROLNA-G", idOnly);
                                                        return;
                                                    }

                                                    // manual/partial typing => not scanned
                                                    setRolnaGScannedByDevice(false);
                                                    setRolnaGRawScan("");

                                                    setRolneGId(raw);
                                                    formik.setFieldValue("ROLNA-G", raw);
                                                }}
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold">POTVRĐUJEM KORIŠTENJE ROLNE G</label>
                                            <select className="form-select" {...formik.getFieldProps("POTVRDJUJEM-KORISTENJE-ROLNE-G")}>
                                                <option value="">ODABERI</option>
                                                <option value="DA">DA</option>
                                                <option value="NE">NE</option>
                                            </select>
                                        </div>

                                        <input
                                            type="hidden"
                                            id="ROLNA-G-SKENIRANA"
                                            name="ROLNA-G-SKENIRANA"
                                            value={formik.values["ROLNA-G-SKENIRANA"] as any}
                                        />
                                    </div>

                                    <div className="row g-3 mt-2">
                                        <div className="col-12 col-lg-8">
                                            <div className={clsx("p-3 rounded border", {"bg-light": !!rolnaGDetalji})}>
                                                <div className="small text-muted fw-semibold mb-1">Detalji rolne</div>
                                                <div className="fw-semibold">{rolnaGDetalji || "—"}</div>
                                            </div>
                                        </div>

                                        <div className="col-12 col-lg-4 d-flex flex-column gap-2">
                                            <ScanStatus interacted={rolnaGInteracted} scanned={rolnaGSkenirana} />

                                            {Number(tolerancijaValjanjaGore) > 0 && (
                                                <div className="alert alert-warning py-2 mb-0">
                                                    <div className="fw-semibold">Potrebna količina materijala za nalog</div>
                                                    <div className="fs-5">
                                                        <strong>{potrebnaKolicinaGore}</strong> kg
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Debug if needed:
                  <div className="mt-2 small text-muted">RAW G: {rolnaGRawScan || "(empty)"}</div>
                  */}
                                </div>
                            </div>
                        </div>

                        {/* ===================== ROLNA D ===================== */}
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge bg-primary">D</span>
                                        <h5 className="mb-0">Donja rolna (nalicje)</h5>
                                    </div>

                                    <div className="d-flex align-items-center gap-2">
                                        {rolnaDInteracted ? (
                                            rolnaDSkenirana ? (
                                                <span className="badge bg-success">Skenirana</span>
                                            ) : (
                                                <span className="badge bg-danger">Nije skenirana</span>
                                            )
                                        ) : (
                                            <span className="badge bg-light text-dark border">Nije provjereno</span>
                                        )}
                                    </div>
                                </div>

                                <div className="card-body">
                                    <div className="row g-3 align-items-end">
                                        <div className="col-12 col-md-2">
                                            <div className="d-flex flex-column gap-2">
                                                <div className="small text-muted fw-semibold">Skeniraj QR kamerom</div>
                                                <QrCodeScanner
                                                    onScan={(data) => {
                                                        applyRolnaDScan(data);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-4">
                                            <label className="form-label fw-bold">ROLNA D</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="ROLNA-D"
                                                {...formik.getFieldProps("ROLNA-D")}
                                                placeholder="Upišite broj rolne"
                                                onFocus={() => (lastFocusedRef.current = "D")}
                                                onChange={(e) => {
                                                    setRolnaDInteracted(true);

                                                    const raw = e.target.value;
                                                    const cleaned = normalizeScan(raw);

                                                    if (isFullRolnaQr(cleaned)) {
                                                        setRolnaDScannedByDevice(true);
                                                        setRolnaDRawScan(cleaned);

                                                        const idOnly = (cleaned.split(";")[0] ?? "").trim();
                                                        setRolneDId(idOnly);
                                                        formik.setFieldValue("ROLNA-D", idOnly);
                                                        return;
                                                    }

                                                    setRolnaDScannedByDevice(false);
                                                    setRolnaDRawScan("");

                                                    setRolneDId(raw);
                                                    formik.setFieldValue("ROLNA-D", raw);
                                                }}
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold">POTVRĐUJEM KORIŠTENJE ROLNE D</label>
                                            <select className="form-select" {...formik.getFieldProps("POTVRDJUJEM-KORISTENJE-ROLNE-D")}>
                                                <option value="">ODABERI</option>
                                                <option value="DA">DA</option>
                                                <option value="NE">NE</option>
                                            </select>
                                        </div>

                                        <input
                                            type="hidden"
                                            id="ROLNA-D-SKENIRANA"
                                            name="ROLNA-D-SKENIRANA"
                                            value={formik.values["ROLNA-D-SKENIRANA"] as any}
                                        />
                                    </div>

                                    <div className="row g-3 mt-2">
                                        <div className="col-12 col-lg-8">
                                            <div className={clsx("p-3 rounded border", {"bg-light": !!rolnaDDetalji})}>
                                                <div className="small text-muted fw-semibold mb-1">Detalji rolne</div>
                                                <div className="fw-semibold">{rolnaDDetalji || "—"}</div>
                                            </div>
                                        </div>

                                        <div className="col-12 col-lg-4 d-flex flex-column gap-2">
                                            <ScanStatus interacted={rolnaDInteracted} scanned={rolnaDSkenirana} />

                                            {Number(tolerancijaValjanjaDole) > 0 && (
                                                <div className="alert alert-warning py-2 mb-0">
                                                    <div className="fw-semibold">Potrebna količina materijala za nalog</div>
                                                    <div className="fs-5">
                                                        <strong>{potrebnaKolicinaDole}</strong> kg
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Debug if needed:
                  <div className="mt-2 small text-muted">RAW D: {rolnaDRawScan || "(empty)"}</div>
                  */}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                        <button type="submit" className="btn btn-primary px-4">
                            Potvrdi
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default ProfilacijaRolne;
